import { slugify } from './slugify';

/**
 * Normalizes property document data for full schema compatibility.
 * This module has ZERO Firebase SDK dependencies — safe for use in public-facing code.
 */
export const normalizeProperty = (docId, data) => {
  const opType = (data.operationType || data.operation || 'venta').toLowerCase();
  const rawPropType = data.propertyType || data.type || 'Casa';
  const propType = rawPropType;
  const priceVal = Number(data.priceMXN || data.price || 0);
  const originalPriceVal = Number(data.originalPriceMXN || data.originalPrice || 0);
  const hasDiscount = Boolean(data.hasDiscount || (originalPriceVal > 0 && originalPriceVal > priceVal));
  const discountPercentage = hasDiscount && originalPriceVal > 0
    ? Math.round(((originalPriceVal - priceVal) / originalPriceVal) * 100)
    : 0;
  const amountSaved = hasDiscount && originalPriceVal > priceVal ? originalPriceVal - priceVal : 0;

  const statusVal = (data.status || 'disponible').toLowerCase();
  const legalStatusVal = (data.legalStatus || 'escriturada').toLowerCase();

  const neighborhood = data.location?.neighborhood || data.location?.colonia || '';
  const city = data.location?.city || 'Hidalgo del Parral';
  const postalCode = data.location?.postalCode || data.location?.zip || '';
  const lat = Number(data.location?.lat || data.location?.latitude || 26.9305);
  const lng = Number(data.location?.lng || data.location?.longitude || -105.6668);

  const hasBedrooms = Boolean(data.features?.hasBedrooms ?? (Number(data.features?.bedrooms || data.specs?.bedrooms || 0) > 0));
  const bedrooms = Number(data.features?.bedrooms || data.specs?.bedrooms || 0);

  const hasBathrooms = Boolean(data.features?.hasBathrooms ?? (Number(data.features?.fullBathrooms || data.features?.bathrooms || data.specs?.bathrooms || 0) > 0));
  const fullBathrooms = Number(data.features?.fullBathrooms || data.features?.bathrooms || data.specs?.bathrooms || 0);
  const halfBathrooms = Number(data.features?.halfBathrooms || 0);

  const hasPatio = Boolean(data.features?.hasPatio || data.features?.patio);
  const hasParking = Boolean(data.features?.hasParking ?? (Number(data.features?.parking || data.features?.parkingSpaces || 0) > 0));
  const parkingSpaces = Number(data.features?.parkingSpaces || data.features?.parking || data.specs?.parking || 0);
  const floors = Number(data.features?.floors || data.features?.levels || data.specs?.floors || data.specs?.levels || 1);

  const landSizeM2 = Number(data.features?.landSizeM2 || data.specs?.landArea || 0);
  const buildSizeM2 = Number(data.features?.buildSizeM2 || data.specs?.buildArea || 0);
  const services = Array.isArray(data.features?.services) ? data.features.services : (Array.isArray(data.services) ? data.services : []);

  const generatedSlug = data.slug ? slugify(data.slug) : (data.title ? slugify(data.title) : docId.toLowerCase());

  return {
    id: docId,
    propertyId: docId,
    title: data.title || 'Propiedad sin título',
    slug: generatedSlug,
    operationType: opType,
    propertyType: propType,
    priceMXN: priceVal,
    originalPriceMXN: originalPriceVal,
    hasDiscount,
    discountPercentage,
    amountSaved,
    // UI Aliases
    operation: opType === 'renta' ? 'Renta' : 'Venta',
    type: propType,
    price: priceVal,
    originalPrice: originalPriceVal,
    currency: data.currency || 'MXN',
    featured: Boolean(data.featured),
    status: statusVal.charAt(0).toUpperCase() + statusVal.slice(1),
    location: {
      address: data.location?.address || `${neighborhood ? neighborhood + ', ' : ''}${city}`,
      colonia: neighborhood,
      neighborhood: neighborhood,
      city: city,
      postalCode: postalCode,
      zip: postalCode,
      state: data.location?.state || 'Chihuahua',
      lat: lat,
      lng: lng
    },
    features: {
      hasBedrooms,
      bedrooms,
      hasBathrooms,
      fullBathrooms,
      halfBathrooms,
      hasPatio,
      hasParking,
      parkingSpaces,
      floors,
      landSizeM2,
      buildSizeM2,
      services
    },
    specs: {
      bedrooms,
      bathrooms: fullBathrooms + (halfBathrooms * 0.5),
      parking: parkingSpaces,
      floors,
      landArea: landSizeM2,
      buildArea: buildSizeM2,
      hasPatio,
      services
    },
    legalStatus: legalStatusVal,
    legal: data.legal || {
      freedomOfLien: 'Certificado de Libertad de Gravamen en Regla',
      titleDeed: 'Escritura Pública Notariada e Inscrita en RPP',
      creditsAccepted: ['Infonavit', 'Bancario', 'Contado'],
      propertyTax: 'Impuesto Predial al Corriente 2026',
      appraisal: 'Avalúo Comercial Certificado',
      cadastreKey: '01-084-012'
    },
    description: data.description || '',
    amenities: Array.isArray(data.amenities) ? data.amenities : [],
    images: Array.isArray(data.images) ? data.images : [],
    analytics: {
      views: data.analytics?.views || 0,
      whatsappClicks: data.analytics?.whatsappClicks || 0,
      leadsCount: data.analytics?.leadsCount || 0
    },
    viewsCount: data.analytics?.views || 0,
    whatsappClicks: data.analytics?.whatsappClicks || 0,
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null
  };
};
